import useSWR from "swr";
import { useApiClient } from "@/hooks/useApiClient";
import { type TrustartTileResponse } from "@/features/map/types/trustart";
import {
  getSignedUrlExpiresSeconds,
  SIGNED_URL_REFRESH_MARGIN_SECONDS,
} from "@/features/map/utils/trustartTileSignedUrl";

// 失効判定はタイル取得側（TrustartChibanCoverageStrokeOverlay の Sentry 通知抑止）と
// 共有する必要があるため utils に置いている。既存の import 元を変えずに済むよう再公開する。
export { SIGNED_URL_REFRESH_MARGIN_SECONDS };

interface UseTrustartTileResult {
  /** 統合タイル（pmtiles）の配信 URL。未取得・失敗・無効時は undefined。 */
  pmtilesUrl: string | undefined;
  isLoading: boolean;
}

// ローカルで生成した pmtiles を直接読むための上書き URL
// （例: http://localhost:8787/parcels.pmtiles）。設定時は BE への
// URL 取得（GET /trustart/tile）と署名切れ再取得を行わない。
// Vite はビルド時に process.env をリテラル展開するため、モジュール定数にしても
// 呼び出し時に読んでも結果は同じ。テストから環境変数を差し替えられるよう関数にしている。
// アロー関数の式本体（`=> process.env...`）にすると展開後のオブジェクトリテラルが
// 関数ブロックとして解釈されビルドが壊れるため、ブロック本体で書く。
const getPmtilesUrlOverride = (): string => {
  return process.env.VITE_TRUSTART_PMTILES_URL ?? "";
};

// 署名 URL の Expires を読めない場合の安全なフォールバック間隔（ms）。
// BE 既定の失効（1時間）より十分短くして期限切れを避ける。
export const FALLBACK_REFRESH_INTERVAL_MS = 50 * 60 * 1000;

/**
 * CloudFront 署名付き URL の Expires(UNIX秒) を読み取り、失効の
 * SIGNED_URL_REFRESH_MARGIN_SECONDS 秒前に再取得するための SWR refreshInterval(ms) を返す。
 * Expires を読めない場合はフォールバック間隔を返す。
 */
export const computeRefreshIntervalMs = (
  pmtilesUrl: string | undefined
): number => {
  if (pmtilesUrl === undefined) {
    return FALLBACK_REFRESH_INTERVAL_MS;
  }
  const expiresSeconds = getSignedUrlExpiresSeconds(pmtilesUrl);
  if (expiresSeconds === undefined) {
    return FALLBACK_REFRESH_INTERVAL_MS;
  }
  const nowSeconds = Date.now() / 1000;
  const secondsUntilRefresh =
    expiresSeconds - nowSeconds - SIGNED_URL_REFRESH_MARGIN_SECONDS;
  // 失効間近／失効済みなら即時（最短1秒）に再取得する。
  return Math.max(secondsUntilRefresh, 1) * 1000;
};

/**
 * TRUSTART 地番地図の統合タイル URL を取得するフック。
 *
 * GET /trustart/tile を叩いて固定名の pmtiles URL（CloudFront 署名付き）を得る。署名には
 * 有効期限（Expires）があり、切れると未取得タイル範囲の取得が 403 になるため、Expires の
 * 手前で自動的に URL を再取得する（URL 変更でレイヤー側が作り直される）。enabled=false
 * （フィーチャーフラグ OFF 等）のときは取得しない。失敗時は undefined を返す。
 * VITE_TRUSTART_PMTILES_URL が設定されている場合は BE を叩かずその URL をそのまま返す
 * （ローカルで生成したタイルの検証用）。
 */
export const useTrustartTile = (enabled: boolean): UseTrustartTileResult => {
  const { apiClient } = useApiClient();
  const pmtilesUrlOverride = getPmtilesUrlOverride();

  const fetchTile = async (url: string): Promise<TrustartTileResponse> => {
    const response = await apiClient.get<TrustartTileResponse>(url);
    return response.data;
  };

  const { data, isLoading } = useSWR(
    enabled && pmtilesUrlOverride === "" ? "/trustart/tile" : null,
    fetchTile,
    {
      revalidateOnFocus: false,
      // 署名 URL の Expires 手前で自動再取得し、期限切れでタイルが 403 になるのを防ぐ。
      refreshInterval: (latestData) =>
        computeRefreshIntervalMs(latestData?.pmtilesUrl),
    }
  );

  if (pmtilesUrlOverride !== "") {
    return {
      pmtilesUrl: enabled ? pmtilesUrlOverride : undefined,
      isLoading: false,
    };
  }

  return { pmtilesUrl: data?.pmtilesUrl, isLoading };
};
